(() => {
  if (!window.customElements || window.customElements.get('cg-category-index')) return;

  const HOVER_DELAY = 110;
  const PRELOAD_CONCURRENCY = 3;

  class CategoryIndex extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._panel = this.querySelector('[data-category-preview]');
      this._browse = this.querySelector('[data-category-browse]');
      this._status = this.querySelector('[data-category-status]');
      this._results = this.querySelector('[data-category-results]');
      if (!this._panel || !this._browse || !this._status || !this._results) return;

      this._connected = true;
      this._listeners = [];
      this._cache = new Map();
      this._images = new Map();
      this._pending = new Map();
      this._queue = [];
      this._activeLoads = 0;
      this._abort = new AbortController();
      this._generation = 0;
      this._usingKeyboard = true;
      this._finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      const count = Number(this.dataset.previewCount);
      this._count = Number.isFinite(count) && count > 0 ? Math.max(2, Math.min(6, Math.floor(count))) : 5;

      // The grid controls expansion; inert keeps the collapsed row out of tab order.
      this._panel.hidden = false;
      this._collapse();
      this._listen(document, 'pointerdown', event => {
        this._usingKeyboard = false;
        this._lastPointerType = event.pointerType;
      }, { passive: true });
      this._listen(document, 'keydown', () => { this._usingKeyboard = true; });
      this._listen(this, 'pointerover', event => this._onPointerOver(event));
      this._listen(this, 'pointerout', event => this._onPointerOut(event));
      this._listen(this, 'pointerleave', event => {
        this._clearHover();
        if (event.pointerType !== 'touch' && this._activationMode === 'hover') this._collapse();
      });
      this._listen(this, 'focusin', event => {
        if (!this._usingKeyboard || this._suppressFocus) return;
        const anchor = this._anchor(event.target);
        if (anchor) this._activate(anchor);
      });
      this._listen(this, 'keydown', event => this._onKeyDown(event));
      this._listen(this, 'click', event => this._onClick(event));
      this.setAttribute('data-ready', '');
      // Warm every small preview, not the full catalog. Keep these decoded
      // images for the lifetime of the section, including unvisited categories.
      this._warmAll();
      this._listen(window, 'resize', () => {
        window.clearTimeout(this._resizeTimer);
        this._resizeTimer = window.setTimeout(() => this._warmAll(), 180);
      }, { passive: true });
    }

    disconnectedCallback() {
      this._connected = false;
      this._collapse();
      this._abort?.abort();
      window.clearTimeout(this._resizeTimer);
      this._queue?.splice(0).forEach(job => job.reject(new DOMException('Disconnected', 'AbortError')));
      this._listeners?.forEach(remove => remove());
      this._listeners = [];
      this._cache?.clear();
      this._images?.clear();
      this.removeAttribute('data-ready');
    }

    _listen(target, type, listener, options) {
      target.addEventListener(type, listener, options);
      this._listeners.push(() => target.removeEventListener(type, listener, options));
    }

    _anchor(target) {
      const anchor = target instanceof Element ? target.closest('a[data-category-tag]') : null;
      return anchor?.closest('cg-category-index') === this ? anchor : null;
    }

    _clearHover() {
      window.clearTimeout(this._hoverTimer);
      this._hoverTimer = null;
    }

    _onPointerOver(event) {
      if (event.pointerType === 'touch' || !this._finePointer.matches) return;
      const anchor = this._anchor(event.target);
      if (!anchor || anchor.contains(event.relatedTarget)) return;
      this._clearHover();
      this._hoverTimer = window.setTimeout(() => {
        this._hoverTimer = null;
        if (this._connected && anchor.isConnected) this._activate(anchor, 'hover');
      }, HOVER_DELAY);
    }

    _onPointerOut(event) {
      const anchor = this._anchor(event.target);
      if (!anchor || anchor.contains(event.relatedTarget)) return;
      this._clearHover();
    }

    _onClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = this._anchor(event.target);
      if (!anchor) return;
      const pointerClick = event.detail > 0 || Boolean(event.pointerType);
      const touchOrCoarse = event.pointerType === 'touch' || this._lastPointerType === 'touch' || !this._finePointer.matches;
      if (pointerClick && touchOrCoarse) {
        event.preventDefault();
        this._activate(anchor, 'touch');
      }
    }

    _onKeyDown(event) {
      if (event.key === 'Escape' && this._activeAnchor) {
        event.preventDefault();
        this._collapse(this._panel.contains(document.activeElement));
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = this._anchor(event.target);
      if (!anchor || (event.key !== ' ' && event.key !== 'ArrowDown')) return;
      event.preventDefault();
      this._activate(anchor).then(() => {
        if (event.key === 'ArrowDown' && this._activeAnchor === anchor && this.classList.contains('is-open')) {
          this._browse.focus({ preventScroll: true });
        }
      });
    }

    _setState(state, message = '') {
      this.dataset.previewState = state;
      this.classList.toggle('is-loading', state === 'loading');
      this._results?.setAttribute('aria-busy', String(state === 'loading'));
      if (this._status) {
        this._status.textContent = message;
        this._status.hidden = !message;
      }
    }

    _collapse(restoreFocus = false) {
      this._clearHover();
      ++this._generation;
      const anchor = this._activeAnchor;
      this._activeAnchor = null;
      this._activationMode = null;
      this.classList.remove('is-open');
      if (this._panel) {
        this._panel.inert = true;
        this._panel.setAttribute('aria-hidden', 'true');
      }
      this.querySelectorAll('a[data-category-tag]').forEach(link => {
        link.classList.remove('is-active');
        link.setAttribute('aria-expanded', 'false');
      });
      // Keep this single inert row while the grid closes, so its height can
      // animate. The next activation replaces it before showing new content.
      this._setState('closed');
      if (restoreFocus && anchor?.isConnected) {
        this._suppressFocus = true;
        try { anchor.focus({ preventScroll: true }); }
        finally { this._suppressFocus = false; }
      }
    }

    async _activate(anchor, mode = 'keyboard') {
      if (!this._connected) return;
      this._clearHover();
      if (this._activeAnchor === anchor && this.classList.contains('is-open')) {
        this._activationMode = mode;
        return;
      }

      const generation = ++this._generation;
      this._activeAnchor?.classList.remove('is-active');
      this._activeAnchor?.setAttribute('aria-expanded', 'false');
      this._activeAnchor = anchor;
      this._activationMode = mode;
      anchor.classList.add('is-active');

      const tag = anchor.dataset.categoryTag;
      const label = anchor.textContent.trim() || tag;
      this._browse.href = anchor.href;
      this._browse.textContent = `Explore ${label} →`;
      this._panel.setAttribute('aria-label', `${label} preview`);
      // Never expose a loading/blank row. The first preview stays compact until
      // both the HTML and the exact responsive image candidates are decoded.
      try {
        const { key } = this._previewKey(anchor);
        let entry = this._cache.get(key);
        if (!entry || entry.viewport !== this._viewport()) {
          // Keep an already-open preview in place until its replacement is decoded.
          entry = await this._ensurePreview(anchor, true);
        }
        if (!this._isCurrent(generation, anchor)) return;
        this._render(entry.cards, label);
        this._open(anchor);
      } catch (error) {
        if (error.name !== 'AbortError' && this._isCurrent(generation, anchor)) {
          this._results.replaceChildren();
          this._setState('error', 'Preview unavailable. Use the Explore link to browse this category.');
          this._open(anchor);
        }
      }
    }

    _open(anchor) {
      anchor.setAttribute('aria-expanded', 'true');
      this._panel.inert = false;
      this._panel.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
    }

    _viewport() { return `${window.innerWidth}:${window.devicePixelRatio || 1}`; }

    _previewKey(anchor) {
      if (!anchor.dataset.previewUrl) throw new Error('Missing category preview URL.');
      const url = new URL(anchor.dataset.previewUrl, window.location.href);
      if (url.origin !== window.location.origin) throw new Error('Invalid category preview origin.');
      url.searchParams.set('section_id', this.dataset.previewSection || 'category-index');
      return { url, key: JSON.stringify([anchor.dataset.categoryTag, url.href]) };
    }

    _warmAll() {
      if (!this._connected) return;
      this.querySelectorAll('a[data-category-tag]').forEach(anchor => {
        this._ensurePreview(anchor).catch(() => {}); // Interaction may retry a failed preload.
      });
    }

    _ensurePreview(anchor, priority = false) {
      let target;
      try { target = this._previewKey(anchor); }
      catch (error) { return Promise.reject(error); }
      const { key } = target;
      const cached = this._cache.get(key);
      if (cached?.viewport === this._viewport()) return Promise.resolve(cached);
      if (this._pending.has(key)) {
        const index = this._queue.findIndex(job => job.key === key);
        if (priority && index > 0) this._queue.unshift(this._queue.splice(index, 1)[0]);
        return this._pending.get(key);
      }
      const promise = new Promise((resolve, reject) => {
        const job = { ...target, tag: anchor.dataset.categoryTag, resolve, reject };
        if (priority) this._queue.unshift(job);
        else this._queue.push(job);
      });
      this._pending.set(key, promise);
      this._drain();
      return promise;
    }

    _drain() {
      while (this._connected && this._activeLoads < PRELOAD_CONCURRENCY && this._queue.length) {
        const job = this._queue.shift();
        const signal = this._abort.signal;
        this._activeLoads++;
        this._loadPreview(job, signal).then(job.resolve, job.reject).finally(() => {
          if (this._abort.signal !== signal) return;
          this._activeLoads--;
          this._pending.delete(job.key);
          this._drain();
        });
      }
    }

    async _loadPreview({ key, url, tag }, signal) {
      let cards = this._cache.get(key)?.cards;
      if (!cards) {
        const response = await fetch(url.href, { signal, credentials: 'same-origin', mode: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Category preview request failed.');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const wrapper = parsed.querySelector('[data-category-response]');
        if (!wrapper || wrapper.dataset.categoryTag !== tag) throw new Error('Category preview did not match the selected category.');
        cards = Array.from(wrapper.children).filter(child => child.localName === 'product-card').slice(0, this._count).map(card => card.outerHTML);
      }
      // A resize during loading must not label old responsive candidates ready.
      let viewport;
      do {
        viewport = this._viewport();
        await this._decodeCards(cards, viewport);
        if (signal.aborted) throw new DOMException('Disconnected', 'AbortError');
      } while (viewport !== this._viewport());
      const entry = { cards, viewport };
      this._cache.set(key, entry);
      return entry;
    }

    _decodeCards(cards, viewport) {
      const template = document.createElement('template');
      template.innerHTML = cards.join('');
      return Promise.all(Array.from(template.content.querySelectorAll('img'), image => {
        const src = image.getAttribute('src');
        const srcset = image.getAttribute('srcset') || '';
        const sizes = image.getAttribute('sizes') || '';
        const key = JSON.stringify([src, srcset, sizes, viewport]);
        if (this._images.has(key)) return this._images.get(key).ready;
        const preload = new Image();
        preload.loading = 'eager';
        preload.decoding = 'async';
        preload.fetchPriority = 'low';
        if (sizes) preload.sizes = sizes;
        if (srcset) preload.srcset = srcset;
        preload.src = src;
        const record = { image: preload };
        record.ready = preload.decode().catch(error => {
          if (this._images.get(key) === record) this._images.delete(key);
          throw error;
        });
        this._images.set(key, record);
        return record.ready;
      }));
    }

    _isCurrent(generation, anchor) {
      return this._connected && this._generation === generation && this._activeAnchor === anchor;
    }

    _render(cards, label) {
      if (!cards.length) {
        this._results.replaceChildren();
        this._setState('empty', `No products to preview in ${label}. Use the Explore link to browse this category.`);
        return;
      }
      const template = document.createElement('template');
      // Use the same lightweight, accessible moving gallery as Newly Added.
      // Short categories opt into enough visual copies to loop without a gap.
      template.innerHTML = `<newly-added-gallery class="newly-added__gallery" data-speed="18" data-repeat-short>
        <div class="newly-added__viewport" tabindex="0" role="group" aria-label="Pattern previews">
          <div class="newly-added__track"><div class="newly-added__group">${cards.slice(0, this._count).join('')}</div></div>
        </div>
      </newly-added-gallery>`;
      template.content.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      this._results.replaceChildren(template.content);
      this._results.scrollLeft = 0;
      this._setState('ready');
    }
  }

  window.customElements.define('cg-category-index', CategoryIndex);
})();
